import React from 'react';
import { useShortSqueeze } from '../hooks/useMarketData';
import { formatTimestamp } from '../utils/formatters';

export const ShortSqueezeDetector: React.FC = () => {
  const { data, isLoading, error } = useShortSqueeze();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-red-600">Failed to load short squeeze analysis</div>
      </div>
    );
  }

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-green-600 bg-green-50 border-green-200';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-red-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Short Squeeze Detection</h3>
        <div className="text-xs text-gray-500">
          Updated: {formatTimestamp(data.timestamp)}
        </div>
      </div>

      {/* Status Banner */}
      <div
        className={`rounded-lg border-2 p-4 mb-6 ${
          data.detected
            ? 'bg-red-50 border-red-300'
            : 'bg-green-50 border-green-300'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="text-3xl">
            {data.detected ? '⚠️' : '✅'}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span
                className={`text-xl font-bold ${
                  data.detected ? 'text-red-700' : 'text-green-700'
                }`}
              >
                {data.detected ? 'Squeeze Signal Detected!' : 'No Squeeze Detected'}
              </span>
              {data.detected && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${getConfidenceColor(
                    data.confidence
                  )}`}
                >
                  {data.confidence.toUpperCase()} Confidence
                </span>
              )}
            </div>
            <p className="text-gray-700">{data.message}</p>
          </div>
        </div>
      </div>

      {/* Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Squeeze Score</span>
          <span className={`text-2xl font-bold ${getScoreColor(data.score)}`}>
            {data.score}/100
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${
              data.score >= 75
                ? 'bg-red-500'
                : data.score >= 50
                ? 'bg-orange-500'
                : 'bg-green-500'
            }`}
            style={{ width: `${data.score}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>0</span>
          <span>50 (Threshold)</span>
          <span>100</span>
        </div>
      </div>

      {/* Indicators */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-700 uppercase">Key Indicators</h4>

        {/* Price Change */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {data.indicators.priceChange.triggered ? '🔥' : '📊'}
            </span>
            <div>
              <div className="font-medium text-gray-800">Price Change</div>
              <div className="text-sm text-gray-600">
                {data.indicators.priceChange.description}
              </div>
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              data.indicators.priceChange.triggered
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {data.indicators.priceChange.value}%
          </div>
        </div>

        {/* Volume Spike */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {data.indicators.volumeSpike.triggered ? '📈' : '📉'}
            </span>
            <div>
              <div className="font-medium text-gray-800">Volume Spike</div>
              <div className="text-sm text-gray-600">
                {data.indicators.volumeSpike.description}
              </div>
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              data.indicators.volumeSpike.triggered
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {data.indicators.volumeSpike.value}x
          </div>
        </div>

        {/* Volatility */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {data.indicators.volatility.triggered ? '⚡' : '💤'}
            </span>
            <div>
              <div className="font-medium text-gray-800">Volatility</div>
              <div className="text-sm text-gray-600">
                {data.indicators.volatility.description}
              </div>
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              data.indicators.volatility.triggered
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {data.indicators.volatility.value}%
          </div>
        </div>

        {/* Momentum */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {data.indicators.momentum.triggered ? '🚀' : '🎯'}
            </span>
            <div>
              <div className="font-medium text-gray-800">Momentum</div>
              <div className="text-sm text-gray-600">
                {data.indicators.momentum.description}
              </div>
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              data.indicators.momentum.triggered
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {data.indicators.momentum.value}%
          </div>
        </div>
      </div>

      {/* Info Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Short squeeze detection analyzes price momentum, volume spikes, volatility, and
          momentum acceleration to identify potential short squeeze conditions. Updates
          every minute.
        </p>
      </div>
    </div>
  );
};
